import { FC, useEffect, useState } from "react";
import {
  useAlertStore,
  useGeneralStore,
  usePopupStore,
  useThemeStore,
  useUserStore,
} from "../../../../stores";
import Table from "../../../../components/table";
import { Customer, CustomerTable, ObjectId, Vec2 } from "../../../../types";
import { TableRendererProps } from "../../../../components/table/types";
import { Text } from "../../../../components/typography";
import Tag from "../../../../components/tag";
import { Row } from "../../../../components/containers";
import { JustifyContent } from "../../../../components/containers/types";
import { CustomerApi } from "../../../../api";
import OptionList from "../../../../components/optionlist";
import { EditIcon, TrashIcon } from "../../../../assets/icons";
import CustomerEditPopup from "../../customer-edit-popup";
import { QuestionBox } from "../../../../popup";
import { CustomerDetails } from "../../../../components/customer";
import useCustomerPageStore from "../../store";

const CustomersTable: FC = () => {
  const theme = useThemeStore((s) => s.theme);
  const customers = useGeneralStore((s) => s.customers);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const getTagById = useGeneralStore((s) => s.getTagById);

  const searchTerm = useCustomerPageStore((s) => s.searchTerm);
  const selectedTagOption = useCustomerPageStore((s) => s.selectedTagOption);
  const [foundCustomers, setFoundCustomers] = useState<Customer[]>(customers);

  useEffect(() => {
    let res: Customer[] = [...customers];

    if (selectedTagOption.title !== "همه") {
      res = res.filter((c) =>
        c.tags.map((t) => t.$oid).includes(selectedTagOption.value.$oid)
      );
    }

    const stringSearchFields: (keyof Customer)[] = [
      "fullname",
      "phone",
      "national_code",
      "address",
      "tags",
    ];

    if (searchTerm) {
      res = res.filter((c) => {
        let v = false;
        stringSearchFields.forEach((k) => {
          if (k === "tags") {
            c[k].forEach((t) => {
              const tag = getTagById(t.$oid);
              if (
                tag &&
                tag.name.toLowerCase().includes(searchTerm.toLowerCase())
              ) {
                v = true;
              }
            });
          } else if (
            String(c[k]).toLowerCase().includes(searchTerm.toLowerCase())
          )
            v = true;
        });
        return v;
      });
    }

    setFoundCustomers(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedTagOption, customers]);

  const [mousePos, setMousePos] = useState<Vec2>({ x: 0, y: 0 });
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );

  const hasUserAccess = useUserStore((s) => s.hasUserAccess);
  const pushAlert = useAlertStore((s) => s.pushAlert);

  const onCustomerEdit = (): void => {
    if (!selectedCustomer) return;
    setPopupBody(<CustomerEditPopup {...selectedCustomer} />);
  };

  const onCustomerDelete = (): void => {
    if (!selectedCustomer) return;
    setPopupBody(
      <QuestionBox
        question={`آیا مطمئن هستید که می خواهید مشتری ${selectedCustomer.fullname} را حذف کنید؟`}
        onYes={() => {
          if (!hasUserAccess(["AddEditCustomer"]))
            return pushAlert("دسترسی غیر مجاز", "error");
          CustomerApi.deleteCustomer(selectedCustomer)
            .then(() => {
              pushAlert("عملیات موفقیت آمیز");
            })
            .catch((err) => pushAlert(err.msg || "unknown error", "error"));
        }}
      />
    );
  };

  return (
    <div
      style={{
        width: "calc(100% - 20px)",
        minHeight: 200,
        background: theme.overlay,
        borderRadius: 15,
        padding: "20px 10px",
        overflowX: "auto",
      }}
    >
      <OptionList
        mousePos={mousePos}
        options={[
          {
            title: "ویرایش",
            onClick: onCustomerEdit,
            Icon: EditIcon,
          },
          {
            title: "حذف",
            onClick: onCustomerDelete,
            Icon: TrashIcon,
            iconColor: theme.alert,
            color: theme.alert,
          },
        ]}
      />
      <Table<CustomerTable>
        data={foundCustomers}
        label={{
          fullname: "نام و نام خانوادگی",
          phone: "شماره تلفن همراه",
          national_code: "کد ملی",
          address: "آدرس",
          tags: "تگ",
        }}
        renderer={{
          address: ({ props }: TableRendererProps) => (
            <Text color={theme.textOnOverlay}>{props as string}</Text>
          ),
          fullname: ({ props }: TableRendererProps) => (
            <Text color={theme.textOnOverlay}>{props as string}</Text>
          ),
          national_code: ({ props }: TableRendererProps) => (
            <Text color={theme.textOnOverlay}>{props as string}</Text>
          ),
          phone: ({ props }: TableRendererProps) => (
            <Text color={theme.textOnOverlay}>{props as string}</Text>
          ),
          tags: ({ props }: TableRendererProps) => (
            <Row justifyContent={JustifyContent.Center} gap={10}>
              {props.length ? <Tag>{props[0] as ObjectId}</Tag> : <></>}
            </Row>
          ),
        }}
        layout={{
          address: 200,
          fullname: 200,
          national_code: 150,
          phone: 120,
          tags: 120,
        }}
        rowEvents={[
          {
            type: "contextmenu",
            handler: (e, c) => {
              e.preventDefault();
              setMousePos({ x: e.clientX, y: e.clientY });
              setSelectedCustomer(c as Customer);
            },
          },
          {
            type: "click",
            handler: (e, data) => {
              setPopupBody(<CustomerDetails customer={data as Customer} />);
            },
          },
        ]}
      />
    </div>
  );
};

export default CustomersTable;
